// The identity page's Voices card, connected (ADR-0077).
//
// The card reads the person's voiceprints and switches references; the pure
// card under `components/identities/voices/` renders what it is given. A viewer
// gets no switch (the read names the role), and a refused switch — the 403 of a
// role that changed since the read, the 409 or 404 of a voice that is no longer
// this identity's, or a plain failure — is the card's `errorKey`.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { canDecideFaces } from '@/features/face-matching/decision-role'
import { confirmedFaceErrorKey, type DecisionErrorKey } from '@/features/face-matching/errors'
import { useEvidenceGraph } from '@/features/face-matching/hooks/useFaceEvidenceGraph'
import { HUMINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import { type IdentityVoicesResponse, identityVoicesApi } from '@/services/api/identity-voices'
import { invalidateVoices, voiceKeys } from './keys'

export interface UseIdentityVoicesResult {
  data: IdentityVoicesResponse | undefined
  isLoading: boolean
  /** The voiceprint whose request is in flight. */
  pendingId: string | null
  errorKey: DecisionErrorKey | null
  /** Without it the switches only show whether a voiceprint is a reference (a viewer). */
  onToggleReference?: (voiceprintId: string, reference: boolean) => void
  onShowInGraph: (voiceprintId: string) => void
}

export function useIdentityVoices(caseId: string, identityId: string): UseIdentityVoicesResult {
  const queryClient = useQueryClient()
  // No read while HUMINT is off: the route answers 503 then (ADR-0051).
  const humint = useExtensionEnabled(HUMINT_EXTENSION_ID)
  const query = useQuery({
    queryKey: voiceKeys.identity(caseId, identityId),
    queryFn: ({ signal }) => identityVoicesApi.list(caseId, identityId, signal),
    enabled: Boolean(caseId) && Boolean(identityId) && humint.enabled,
    staleTime: 60 * 1000,
  })
  const evidence = useEvidenceGraph(caseId, {
    fetch: voiceprintId => identityVoicesApi.evidence(caseId, identityId, voiceprintId),
    label: 'identity.voices.graphEvidence',
  })

  const reference = useMutation({
    mutationFn: ({ voiceprintId, reference }: { voiceprintId: string; reference: boolean }) =>
      identityVoicesApi.setReference(caseId, identityId, voiceprintId, reference),
    // The answer is the refreshed card: keep it, and let the other reads re-read.
    onSuccess: refreshed => {
      queryClient.setQueryData(voiceKeys.identity(caseId, identityId), refreshed)
      invalidateVoices(queryClient, caseId, { keepIdentity: identityId })
    },
    // Refused: the card is read again, so a voice that is no longer this identity's leaves it.
    onError: () =>
      void queryClient.invalidateQueries({ queryKey: voiceKeys.identity(caseId, identityId) }),
  })
  const toggle = useCallback(
    (voiceprintId: string, next: boolean) => reference.mutate({ voiceprintId, reference: next }),
    [reference]
  )

  return {
    data: query.data,
    isLoading: query.isLoading,
    pendingId:
      (reference.isPending ? reference.variables?.voiceprintId : null) ??
      (evidence.isPending ? evidence.variables : null) ??
      null,
    errorKey: confirmedFaceErrorKey(reference.error),
    onToggleReference: canDecideFaces(query.data?.member_role) ? toggle : undefined,
    onShowInGraph: voiceprintId => evidence.mutate(voiceprintId),
  }
}
